/**
 * Provider Form Page - Create/Edit telephony providers.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Skeleton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Save,
  Key,
  Cloud,
  Phone,
  Public,
  Hub,
  Speed,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import DetailHeader from '../components/DetailHeader';
import type { AIAgentProvider, ProviderTypeOption } from '../types';
import usePageTitle from '../hooks/usePageTitle';

export default function ProviderFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [providerTypes, setProviderTypes] = useState<ProviderTypeOption[]>([]);
  const [existingProvider, setExistingProvider] = useState<AIAgentProvider | null>(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsResult, setDiagnosticsResult] = useState<Record<string, unknown> | null>(null);
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    providerType: '',
    scope: 'tenant' as 'global' | 'tenant',
    description: '',
    isActive: true,
    maxConcurrentCalls: 10,
    // Standard credentials
    apiKey: '',
    webhookSecret: '',
  });
  const providerDescriptor = existingProvider?.name || formData.name || undefined;
  usePageTitle(t('pages:providers.title'), providerDescriptor);

  const fetchInitialDataRef = useRef<() => Promise<void>>(async () => undefined);

  useEffect(() => {
    void fetchInitialDataRef.current();
  }, [id]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch provider types
      const typesRes = await api.get('/api/ai-agents-providers/types/');
      setProviderTypes(typesRes.data.types ?? []);

      // If editing, fetch provider data
      if (id) {
        const providerRes = await api.get(`/api/ai-agents-providers/${id}/`);
        const provider = providerRes.data;
        setExistingProvider(provider);
        
        setFormData({
          name: provider.name,
          providerType: provider.providerType,
          scope: provider.scope || 'tenant',
          description: provider.description || '',
          isActive: provider.isActive,
          maxConcurrentCalls: provider.maxConcurrentCalls ?? 10,
          // Leave credentials blank for security
          apiKey: '',
          webhookSecret: '',
        });
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      console.error('Error loading data:', err);
      setError(error?.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  fetchInitialDataRef.current = fetchInitialData;

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload: Record<string, unknown> = {
        name: formData.name,
        providerType: formData.providerType,
        scope: formData.scope,
        description: formData.description,
        isActive: formData.isActive,
        maxConcurrentCalls: Number(formData.maxConcurrentCalls) || 10,
      };

      // Build credentials
      const credentials: Record<string, string | number> = {};

      if (formData.apiKey) credentials.apiKey = formData.apiKey;
      if (formData.webhookSecret) credentials.webhookSecret = formData.webhookSecret;

      if (Object.keys(credentials).length > 0) {
        payload.credentials = credentials;
      }

      if (isEditing) {
        await api.patch(`/api/ai-agents-providers/${id}/`, payload);
        setSuccess(t('pages:providers.updateSuccess') || 'Provider updated successfully');
      } else {
        await api.post('/api/ai-agents-providers/', payload);
        setSuccess(t('pages:providers.createSuccess') || 'Provider created successfully');
        setTimeout(() => navigate('/providers'), 1500);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string; error?: string } } };
      console.error('Error saving provider:', err);
      setError(error?.response?.data?.detail || error?.response?.data?.error || t('pages:providers.errors.saving'));
    } finally {
      setSaving(false);
    }
  };

  const handleDiagnostics = async () => {
    if (!id) return;
    try {
      setDiagnosticsLoading(true);
      setDiagnosticsError(null);
      setDiagnosticsResult(null);
      const response = await api.post(`/api/ai-agents-providers/${id}/diagnostics/`);
      setDiagnosticsResult(response.data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string; error?: string } } };
      setDiagnosticsError(
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Failed to run diagnostics'
      );
    } finally {
      setDiagnosticsLoading(false);
    }
  };

  const canSubmit = () => {
    if (!formData.name || !formData.providerType) return false;
    return true;
  };

  const getProviderIcon = (type: string) => {
    switch (type) {
      case 'cloud':
        return <Cloud />;
      default:
        return <Phone />;
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  const headerTitle = isEditing
    ? existingProvider?.name || t('pages:providers.editTitle')
    : t('pages:providers.newTitle');

  const breadcrumbs = [
    { label: t('common:menu.providers'), to: '/providers' },
    { label: isEditing ? existingProvider?.name || t('pages:providers.edit') : t('pages:providers.new') },
  ];

  const headerActions = (
    <Button
      variant="contained"
      startIcon={
        saving ? <CircularProgress size={20} color="inherit" /> : <Save />
      }
      onClick={handleSubmit}
      disabled={saving || !canSubmit()}
    >
      {saving ? t('common:actions.saving') : t('common:actions.save')}
    </Button>
  );

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={<Hub sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={existingProvider?.id ?? null}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/providers"
      />

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Basic Info */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Key />
                {t('pages:providers.basicInfo') || 'Basic Information'}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label={t('pages:providers.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                  disabled={saving}
                />

                <TextField
                  label={t('pages:providers.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={saving}
                />

                <FormControl fullWidth required disabled={isEditing || saving}>
                  <InputLabel>{t('pages:providers.providerType')}</InputLabel>
                  <Select
                    value={formData.providerType}
                    label={t('pages:providers.providerType')}
                    onChange={(e) => setFormData({ ...formData, providerType: e.target.value })}
                  >
                    {providerTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getProviderIcon(type.value)}
                          <span>{type.label}</span>
                          {type.supportsAgents && (
                            <Chip label="+ Agents" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth disabled={saving}>
                  <InputLabel>{t('pages:providers.scopeLabel')}</InputLabel>
                  <Select
                    value={formData.scope}
                    label={t('pages:providers.scopeLabel')}
                    onChange={(e) => setFormData({ ...formData, scope: e.target.value as 'global' | 'tenant' })}
                  >
                    <MenuItem value="tenant">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Hub fontSize="small" />
                        {t('pages:providers.scope.tenant')}
                      </Box>
                    </MenuItem>
                    <MenuItem value="global">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Public fontSize="small" />
                        {t('pages:providers.scope.global')}
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('pages:providers.isActive')}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                  <TextField
                    label={t('pages:providers.maxConcurrentCalls')}
                    type="number"
                    value={formData.maxConcurrentCalls}
                    onChange={(e) => setFormData({ ...formData, maxConcurrentCalls: Math.max(1, Number(e.target.value) || 1) })}
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText={t('pages:providers.maxConcurrentCallsHelper')}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Credentials */}
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Key />
                  {t('pages:providers.credentials')}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {!formData.providerType ? (
                  <Alert severity="info">
                    Select a provider type to configure credentials.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formData.providerType && (
                      <Alert severity="info">
                        {t(`pages:providers.providerInfo.${formData.providerType}` as never)}
                      </Alert>
                    )}
                    
                    {formData.providerType === 'cloud' && (
                      <TextField
                        label={t('pages:providers.apiKey')}
                        type="password"
                        value={formData.apiKey}
                        onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                        fullWidth
                        placeholder={isEditing ? '(leave blank to keep current)' : ''}
                        helperText={t('pages:providers.apiKeyHelper')}
                      />
                    )}
                    
                    {formData.providerType === 'cloud' && (
                      <TextField
                        label={t('pages:providers.webhookSecret')}
                        type="password"
                        value={formData.webhookSecret}
                        onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
                        fullWidth
                        placeholder={isEditing ? '(leave blank to keep current)' : ''}
                        helperText={t('pages:providers.webhookSecretHelper')}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {isEditing && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Speed />
                    Diagnostics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Runs connectivity checks for the credentials currently saved in this provider.
                  </Typography>

                  <Button
                    variant="outlined"
                    onClick={handleDiagnostics}
                    disabled={diagnosticsLoading || saving}
                  >
                    {diagnosticsLoading ? 'Running diagnostics...' : 'Run diagnostics'}
                  </Button>

                  {diagnosticsError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDiagnosticsError(null)}>
                      {diagnosticsError}
                    </Alert>
                  )}

                  {diagnosticsResult && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontSize: 12,
                        overflow: 'auto',
                        maxHeight: 320,
                      }}
                    >
                      {JSON.stringify(diagnosticsResult, null, 2)}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
